import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Minus, User, Phone, FileText, Clock, AlertCircle, Users, ChevronDown, Search, CreditCard, DollarSign, Smartphone, ShoppingCart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, MenuItem, OrderItem, Order, Table } from '../../services/dataService';
import AddOnsSelectionModal from './AddOnsSelectionModal';
import { createCheckoutIdempotencyKey } from '../../utils/checkoutIdempotency';

interface QuickCustomer {
  name: string;
  phone?: string;
  customerId: string;
  orderId: string;
  remainingAmount: number;
  itemCount: number;
  lastOrderDate: Date;
}

interface CustomerSuggestion {
  name: string;
  phone?: string;
  customerId?: string;
  orderCount: number;
  lastOrderDate: Date;
  totalSpent: number;
}

interface AddOrderModalProps {
  currentTab: 'requested' | 'preparing' | 'completed';
  onClose: () => void;
  onSuccess: () => void;
  posSimplified?: boolean;
  onOptimisticOrderCreate?: (
    customer: string,
    customerPhone: string | undefined,
    items: Array<{
      menuItem: any;
      quantity: number;
      notes: string;
    }>,
    currentTab: 'requested' | 'preparing' | 'completed',
    tableNumber?: string,
    tableId?: string,
    notes?: string,
    paymentMethod?: 'cash' | 'card' | 'digital',
    idempotencyKey?: string
  ) => Promise<void>;
}

const AddOrderModal: React.FC<AddOrderModalProps> = ({ currentTab, onClose, onSuccess, onOptimisticOrderCreate, posSimplified = false }) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [addingItemId, setAddingItemId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [itemSalesData, setItemSalesData] = useState<Map<string, number>>(new Map());
  const [quickCustomers, setQuickCustomers] = useState<QuickCustomer[]>([]);
  const [showQuickCustomers, setShowQuickCustomers] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState<CustomerSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<CustomerSuggestion[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [isCustomerSelectionMode, setIsCustomerSelectionMode] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const submitInFlightRef = useRef(false);
  
  // Menu filtering and search
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Customer search term (separate from menu search)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  
  // Payment method state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'cash' | 'card' | 'digital'>('digital');
  
  // Guest order state
  const [isGuestOrder, setIsGuestOrder] = useState(false);
  
  const [formData, setFormData] = useState({
    customer: '',
    customerPhone: '',
    tableNumber: '',
    tableId: '' as string | undefined, // Add table ID for proper status tracking
    notes: ''
  });

  const [selectedItems, setSelectedItems] = useState<{
    menuItem: MenuItem;
    quantity: number;
    notes: string;
    addOnSelections?: Array<{
      categoryId: string;
      categoryName: string;
      selectedOptions: Array<{
        optionId: string;
        optionName: string;
        price: number;
      }>;
    }>;
  }[]>([]);

  // Add-ons modal state
  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [pendingMenuItem, setPendingMenuItem] = useState<MenuItem | null>(null);

  // Table selection state
  const [tables, setTables] = useState<Table[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);

  const loadMenuItems = useCallback(async () => {
    try {
      const items = await dataService.getAvailableMenuItems(tenantData!.id);
      setMenuItems(items);
    } catch (error) {
      console.error('Error loading menu items:', error);
    }
  }, [tenantData]);

  const loadTables = useCallback(async () => {
    try {
      setTablesLoading(true);
      const tablesData = await dataService.getTables(tenantData!.id);
      
  
      // Filter only active and available tables
      const availableTables = tablesData.filter(table => 
        table.isActive && table.status === 'available'
      );
  
      setTables(availableTables);
    } catch (error) {
      console.error('Error loading tables:', error);
    } finally {
      setTablesLoading(false);
    }
  }, [tenantData]);

  // Load item sales data to calculate popularity
  const loadItemSalesData = useCallback(async () => {
    try {
      const orders = await dataService.getOrders(tenantData!.id);
      const salesMap = new Map<string, number>();
      
      // Calculate total quantity sold for each menu item
      orders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            const currentCount = salesMap.get(item.menuItemId) || 0;
            salesMap.set(item.menuItemId, currentCount + item.quantity);
          });
        }
      });
      
      setItemSalesData(salesMap);
    } catch (error) {
      console.error('Error loading item sales data:', error);
    }
  }, [tenantData]);

  // Get unique categories from menu items
  const getMenuCategories = useCallback(() => {
    const categories = Array.from(new Set(menuItems.map(item => item.category))).sort();
    return ['all', ...categories];
  }, [menuItems]);

  // Filter menu items based on search and category
  const getFilteredMenuItems = useCallback(() => {
    let filteredItems = menuItems.filter(item => {
      const matchesSearch = menuSearchTerm === '' || 
        item.name.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(menuSearchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });

    // Sort by most selling items when "all" category is selected
    if (selectedCategory === 'all') {
      filteredItems = filteredItems.sort((a, b) => {
        const salesA = itemSalesData.get(a.id!) || 0;
        const salesB = itemSalesData.get(b.id!) || 0;
        
        // Sort by sales count (descending), then by name (ascending) for ties
        if (salesB !== salesA) {
          return salesB - salesA;
        }
        return a.name.localeCompare(b.name);
      });
    } else {
      // For specific categories, sort alphabetically by name
      filteredItems = filteredItems.sort((a, b) => a.name.localeCompare(b.name));
    }

    return filteredItems;
  }, [menuItems, menuSearchTerm, selectedCategory, itemSalesData]);

  const loadQuickCustomers = useCallback(async () => {
    try {
      const orders = await dataService.getOrders(tenantData!.id);
      
      // Filter orders that are active and not fully paid
      const openOrders = orders.filter(order => 
        order.status === 'active' && 
        !order.isPaid && 
        order.remainingAmount > 0 &&
        order.customer && // Ensure customer exists
        typeof order.customer === 'string' // Ensure customer is a string
      );

      // Group by customer and get the latest order info
      const customerMap = new Map<string, QuickCustomer>();
      
      openOrders.forEach(order => {
        const customerKey = order.customer.toLowerCase();
        const existing = customerMap.get(customerKey);
        
        if (!existing || new Date(order.createdAt!) > existing.lastOrderDate) {
          customerMap.set(customerKey, {
            name: order.customer,
            phone: order.customerPhone,
            customerId: order.customerId || order.id!,
            orderId: order.id!,
            remainingAmount: order.remainingAmount,
            itemCount: order.items?.length || 0,
            lastOrderDate: order.createdAt instanceof Date ? order.createdAt : new Date(order.createdAt!)
          });
        }
      });

      // Convert to array and sort by most recent
      const customers = Array.from(customerMap.values())
        .sort((a, b) => b.lastOrderDate.getTime() - a.lastOrderDate.getTime())
        .slice(0, 10); // Limit to 10 most recent

      setQuickCustomers(customers);
    } catch (error) {
      console.error('Error loading quick customers:', error);
    }
  }, [tenantData]);

  const loadCustomerSuggestions = useCallback(async () => {
    try {
      const orders = await dataService.getOrders(tenantData!.id);
      const payments = await dataService.getPayments(tenantData!.id);
      const customers = await dataService.getCustomers(tenantData!.id);
      
      // Group orders by customer
      const customerMap = new Map<string, CustomerSuggestion>();
      
      // First, add customers from the customers collection
      customers.forEach(customer => {
        if (!customer.name || typeof customer.name !== 'string') {
          return;
        }
        
        const customerKey = customer.name.toLowerCase();
        customerMap.set(customerKey, {
          name: customer.name,
          phone: customer.phone,
          customerId: customer.id,
          orderCount: customer.totalOrders || 0,
          lastOrderDate: customer.lastOrderDate instanceof Date ? 
            customer.lastOrderDate : 
            (customer.lastOrderDate ? new Date(customer.lastOrderDate) : 
             (customer.dateJoined instanceof Date ? customer.dateJoined : new Date(customer.dateJoined || Date.now()))),
          totalSpent: customer.totalSpent || 0
        });
      });
      
      // Then, update with order data (this will override customer collection data with more recent info)
      orders.forEach(order => {
        // Skip orders without customer names
        if (!order.customer || typeof order.customer !== 'string') {
          return;
        }
        
        const customerKey = order.customer.toLowerCase();
        const existing = customerMap.get(customerKey);
        
        // Calculate total spent for this customer
        const customerPayments = payments.filter(payment => {
          const orderForPayment = orders.find(o => o.id === payment.orderId);
          return orderForPayment?.customer && 
                 orderForPayment.customer.toLowerCase() === customerKey;
        });
        const totalSpent = customerPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
        
        if (!existing) {
          customerMap.set(customerKey, {
            name: order.customer,
            phone: order.customerPhone,
            customerId: order.customerId,
            orderCount: 1,
            lastOrderDate: order.createdAt instanceof Date ? order.createdAt : new Date(order.createdAt!),
            totalSpent
          });
        } else {
          existing.orderCount += 1;
          const orderDate = order.createdAt instanceof Date ? order.createdAt : new Date(order.createdAt!);
          if (orderDate > existing.lastOrderDate) {
            existing.lastOrderDate = orderDate;
            existing.name = order.customer; // Use most recent name format
            if (order.customerPhone && !existing.phone) {
              existing.phone = order.customerPhone;
            }
            // Update customerId if not set or if this order has a more recent customerId
            if (order.customerId && !existing.customerId) {
              existing.customerId = order.customerId;
            }
          }
          existing.totalSpent = totalSpent;
        }
      });

      // Convert to array and sort by order count and recency
      const suggestions = Array.from(customerMap.values())
        .sort((a, b) => {
          // First sort by order count (frequent customers first)
          if (b.orderCount !== a.orderCount) {
            return b.orderCount - a.orderCount;
          }
          // Then by recency - both should be Date objects now
          return b.lastOrderDate.getTime() - a.lastOrderDate.getTime();
        });

      setCustomerSuggestions(suggestions);
    } catch (error) {
      console.error('Error loading customer suggestions:', error);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadMenuItems();
      loadItemSalesData();
      loadQuickCustomers();
      loadCustomerSuggestions();
      loadTables();
    }
  }, [tenantData, loadMenuItems, loadItemSalesData, loadQuickCustomers, loadCustomerSuggestions, loadTables]);

  // Close table dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showTableDropdown && !target.closest('.table-dropdown')) {
        setShowTableDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTableDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle customer name autocomplete/search
    if (name === 'customer') {
      if (value.trim().length > 0) {
        const filtered = customerSuggestions.filter(customer =>
          customer.name && customer.name.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 12); // Show more results when searching
        setFilteredSuggestions(filtered);
        setShowSuggestions(true);
        setIsSearchMode(true);
      } else {
        // Show all customers when field is empty but focused
        if (isSearchMode) {
          setFilteredSuggestions(customerSuggestions.slice(0, 15)); // Show top 15 customers
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
          setFilteredSuggestions([]);
        }
      }
    }
  };

  const handleCustomerFocus = () => {
    setIsSearchMode(true);
    if (formData.customer.trim().length > 0) {
      const filtered = customerSuggestions.filter(customer =>
        customer.name && customer.name.toLowerCase().includes(formData.customer.toLowerCase())
      ).slice(0, 12);
      setFilteredSuggestions(filtered);
    } else {
      // Show all customers when field is empty
      setFilteredSuggestions(customerSuggestions.slice(0, 15));
    }
    setShowSuggestions(true);
  };

  const handleCustomerBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setIsSearchMode(false);
    }, 200);
  };

  const selectCustomerSuggestion = (customer: CustomerSuggestion) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.name,
      customerPhone: customer.phone || ''
    }));
    setShowSuggestions(false);
    setFilteredSuggestions([]);
    setIsSearchMode(false);
    setShowCustomerForm(false);
    setIsCustomerSelectionMode(false);
    setCustomerSearchTerm('');
  };

  const selectQuickCustomer = (customer: QuickCustomer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer.name,
      customerPhone: customer.phone || ''
    }));
    setShowQuickCustomers(false);
    setShowCustomerForm(false);
    setIsCustomerSelectionMode(false);
    setCustomerSearchTerm('');
  };

  const clearCustomerSearch = () => {
    setFormData(prev => ({
      ...prev,
      customer: '',
      customerPhone: ''
    }));
    setShowSuggestions(false);
    setFilteredSuggestions([]);
    setIsSearchMode(false);
    setCustomerSearchTerm('');
  };

  const selectTable = (table: Table) => {
    setFormData(prev => ({
      ...prev,
      tableNumber: table.tableNumber,
      tableId: table.id // Add table ID for proper status tracking
    }));
    setShowTableDropdown(false);
  };

  // Filter customers based on search term
  const getFilteredCustomers = useCallback(() => {
    if (!customerSearchTerm.trim()) return customerSuggestions;
    
    return customerSuggestions.filter(customer =>
      customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      (customer.phone && customer.phone.toLowerCase().includes(customerSearchTerm.toLowerCase()))
    );
  }, [customerSuggestions, customerSearchTerm]);

  const addMenuItem = async (menuItem: MenuItem) => {
    // Prevent adding if already processing this item
    if (addingItemId === menuItem.id) return;
    
    setAddingItemId(menuItem.id!);
    
    try {
      // Check if this menu item has add-ons configured
      if (tenantData?.id) {
        const addOnsConfig = await dataService.getMenuItemAddOns(tenantData.id, menuItem.id!);
        
        if (addOnsConfig && addOnsConfig.addOnCategories && addOnsConfig.addOnCategories.length > 0) {
          // Show add-ons selection modal
          setPendingMenuItem(menuItem);
          setShowAddOnsModal(true);
          return;
        }
      }
      
      // No add-ons, add item directly
      addItemToSelection(menuItem);
    } catch (error) {
      console.error('Error checking add-ons:', error);
      // Fallback to adding item directly
      addItemToSelection(menuItem);
    } finally {
      // Clear loading state after a short delay to prevent rapid clicking
      setTimeout(() => {
        setAddingItemId(null);
      }, 300);
    }
  };

  const addItemToSelection = (menuItem: MenuItem, addOnSelections?: Array<{
    categoryId: string;
    categoryName: string;
    selectedOptions: Array<{
      optionId: string;
      optionName: string;
      price: number;
    }>;
  }>) => {
    // Check if item with same add-ons already exists
    const existingIndex = selectedItems.findIndex(item => {
      // First check if it's the same menu item
      if (item.menuItem.id !== menuItem.id) return false;
      
      // If no add-ons on either, treat as same item
      if (!item.addOnSelections && !addOnSelections) return true;
      
      // If one has add-ons and the other doesn't, treat as different items
      if (!item.addOnSelections || !addOnSelections) return false;
      
      // If both have add-ons, check if they're identical
      if (item.addOnSelections.length !== addOnSelections.length) return false;
      
      // Check if all categories and options match exactly
      return item.addOnSelections.every((itemCategory, index) => {
        const newCategory = addOnSelections[index];
        if (itemCategory.categoryId !== newCategory.categoryId) return false;
        if (itemCategory.selectedOptions.length !== newCategory.selectedOptions.length) return false;
        
        return itemCategory.selectedOptions.every((itemOption, optIndex) => {
          const newOption = newCategory.selectedOptions[optIndex];
          return itemOption.optionId === newOption.optionId;
        });
      });
    });
    
    if (existingIndex >= 0) {
      // Increase quantity if item with identical add-ons already exists
      setSelectedItems(prev => prev.map((item, index) => 
        index === existingIndex 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      // Add new item (either different menu item or same item with different add-ons)
      setSelectedItems(prev => [...prev, {
        menuItem,
        quantity: 1,
        notes: '',
        addOnSelections
      }]);
    }
  };

  const handleAddOnsConfirm = (selectedAddOns: Array<{
    categoryId: string;
    categoryName: string;
    selectedOptions: Array<{
      optionId: string;
      optionName: string;
      price: number;
    }>;
  }>) => {
    if (pendingMenuItem) {
      addItemToSelection(pendingMenuItem, selectedAddOns);
      setPendingMenuItem(null);
      setShowAddOnsModal(false);
    }
  };

  const handleAddOnsCancel = () => {
    setPendingMenuItem(null);
    setShowAddOnsModal(false);
    setAddingItemId(null);
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(index);
      return;
    }
    
    setSelectedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, quantity } : item
    ));
  };

  const addSameItemWithAddOns = async (index: number) => {
    const existingItem = selectedItems[index];
    if (!existingItem || !tenantData?.id) return;
    
    try {
      // Check if this menu item has add-ons configured
      const addOnsConfig = await dataService.getMenuItemAddOns(tenantData.id, existingItem.menuItem.id!);
      
      if (addOnsConfig && addOnsConfig.addOnCategories && addOnsConfig.addOnCategories.length > 0) {
        // Show add-ons selection modal for the same item
        setPendingMenuItem(existingItem.menuItem);
        setShowAddOnsModal(true);
      } else {
        // No add-ons, just increase quantity
        setSelectedItems(prev => prev.map((item, i) => 
          i === index ? { ...item, quantity: item.quantity + 1 } : item
        ));
      }
    } catch (error) {
      console.error('Error checking add-ons:', error);
      // Fallback to increasing quantity
      setSelectedItems(prev => prev.map((item, i) => 
        i === index ? { ...item, quantity: item.quantity + 1 } : item
      ));
    }
  };

  const updateItemNotes = (index: number, notes: string) => {
    setSelectedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, notes } : item
    ));
  };

  const removeItem = (index: number) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const subtotal = selectedItems.reduce((sum, item) => {
      let itemTotal = item.menuItem.price * item.quantity;
      
      // Add add-ons pricing
      if (item.addOnSelections) {
        item.addOnSelections.forEach(category => {
          category.selectedOptions.forEach(option => {
            itemTotal += option.price * item.quantity;
          });
        });
      }
      
      return sum + itemTotal;
    }, 0);
    
    return {
      subtotal,
      tax: 0, // No tax
      total: subtotal // Total equals subtotal
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitInFlightRef.current) {
      return;
    }

    if (!isGuestOrder && !formData.customer.trim()) {
      setError('Please enter customer name or select Guest Order');
      return;
    }

    if (selectedItems.length === 0) {
      setError('Please add at least one item to the order');
      return;
    }

    const idempotencyKey = createCheckoutIdempotencyKey('order');
    submitInFlightRef.current = true;

    // Use optimistic UI if available, otherwise fallback to original method
    if (onOptimisticOrderCreate) {
      try {
        setError('');
        
        console.log(`🎬 [${Date.now()}] Modal: Starting order submission for ${formData.customer}`);
        
        // Close modal FIRST, then start optimistic update
        onSuccess();
        onClose();
        
        console.log(`📞 [${Date.now()}] Modal: Calling optimistic order create function`);
        // Call optimistic function in background (don't await!)
        // Only pass customerPhone if it's not empty
        const customerPhone = formData.customerPhone.trim();
        onOptimisticOrderCreate(
          isGuestOrder ? 'Guest' : formData.customer.trim(),
          isGuestOrder ? undefined : (customerPhone || undefined),
          selectedItems,
          currentTab,
          formData.tableNumber.trim() || undefined,
          formData.tableId,
          formData.notes.trim() || undefined,
          selectedPaymentMethod,
          idempotencyKey
        ).catch(error => {
          console.error('Error in optimistic order creation:', error);
          // The optimistic function handles its own error display
        }).finally(() => {
          submitInFlightRef.current = false;
        });
        console.log(`🏁 [${Date.now()}] Modal: Optimistic function called, modal work done`);
      } catch (error) {
        submitInFlightRef.current = false;
        console.error('Error setting up optimistic order creation:', error);
        setError('Failed to create order. Please try again.');
      }
    } else {
      // Fallback to original method
      try {
        setLoading(true);
        setError('');

        const checkoutOptions = { idempotencyKey };

        // Get or create active order for customer
        let order: Order;
        if (isGuestOrder) {
          order = await dataService.createGuestOrder(
            tenantData!.id,
            formData.tableNumber.trim() || undefined,
            formData.tableId, // Pass table ID for proper status tracking
            formData.notes.trim() || undefined,
            checkoutOptions
          );
        } else {
          // Only pass customerPhone if it's not empty
          const customerPhone = formData.customerPhone.trim();
          order = await dataService.getOrCreateActiveOrder(
            tenantData!.id,
            formData.customer.trim(),
            customerPhone || undefined,
            formData.tableNumber.trim() || undefined,
            formData.tableId, // Pass table ID for proper status tracking
            checkoutOptions
          );
        }

        // Add each selected item to the order
        for (const selectedItem of selectedItems) {
          let totalPrice = selectedItem.menuItem.price * selectedItem.quantity;
          
          // Calculate add-ons pricing
          if (selectedItem.addOnSelections) {
            selectedItem.addOnSelections.forEach(category => {
              category.selectedOptions.forEach(option => {
                totalPrice += option.price * selectedItem.quantity;
              });
            });
          }
          
          const orderItem: OrderItem = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            menuItemId: selectedItem.menuItem.id!,
            menuItemName: selectedItem.menuItem.name,
            quantity: selectedItem.quantity,
            unitPrice: selectedItem.menuItem.price,
            totalPrice: totalPrice,
            status: currentTab, // Set status based on current tab
            addOnSelections: selectedItem.addOnSelections // Include add-ons data
          };

          // Only add notes if they exist and are not empty
          if (selectedItem.notes && selectedItem.notes.trim()) {
            orderItem.notes = selectedItem.notes.trim();
          }

          await dataService.addItemToOrder(tenantData!.id, order.id!, orderItem);
        }

        // Update order with additional info if provided
        if (formData.tableNumber || formData.tableId || formData.notes) {
          const updateData: Partial<Order> = {};
          
          if (formData.tableNumber.trim()) {
            updateData.tableNumber = formData.tableNumber.trim();
          }
          
          if (formData.tableId) {
            updateData.tableId = formData.tableId;
          }
          
          if (formData.notes.trim()) {
            updateData.notes = formData.notes.trim();
          }
          
          if (Object.keys(updateData).length > 0) {
            await dataService.update<Order>(tenantData!.id, 'orders', order.id!, updateData);
          }
        }

        // Process payment if cash is selected
        if (selectedPaymentMethod === 'cash') {
          const { total } = calculateTotal();
          await dataService.addPaymentToOrder(
            tenantData!.id,
            order.id!,
            total,
            'cash',
            undefined,
            { idempotencyKey: createCheckoutIdempotencyKey('payment') }
          );
        }

        // Close modal after successful creation
        onSuccess();
        onClose();
      } catch (error) {
        console.error('Error creating order:', error);
        setError('Failed to create order. Please try again.');
        setLoading(false);
      } finally {
        submitInFlightRef.current = false;
      }
    }
  };

  const { subtotal, tax, total } = calculateTotal();

  const paymentMethods = [
    { value: 'card', label: 'Credit Card', icon: CreditCard, color: 'bg-blue-500' },
    { value: 'digital', label: 'Paylater', icon: Smartphone, color: 'bg-gray-500' },
    { value: 'cash', label: 'Cash Payout', icon: DollarSign, color: 'bg-green-500' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl md:h-[90vh] h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center space-x-2 md:space-x-4">
            {isCustomerSelectionMode ? (
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                Select Customer
              </h2>
            ) : posSimplified ? (
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                New Order
              </h2>
            ) : (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400 mb-1">New Order</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  currentTab === 'requested' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400' :
                  currentTab === 'preparing' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' :
                  'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                }`}>
                  {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)} Tab
                </span>
              </div>
            )}
          </div>
          
          {/* Search Bar in Header */}
          {!isCustomerSelectionMode && (
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={menuSearchTerm}
                  onChange={(e) => setMenuSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search Your Menu Here"
                />
              </div>
            </div>
          )}

          {/* Customer Search in Header (when in customer selection mode) */}
          {isCustomerSelectionMode && (
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search customers..."
                />
              </div>
            </div>
          )}
          
          {/* Customer Info / Actions */}
          <div className="flex items-center space-x-2">
            {/* Mobile Cart Toggle */}
            {!isCustomerSelectionMode && selectedItems.length > 0 && (
              <button
                onClick={() => setShowMobileCart(!showMobileCart)}
                className="md:hidden bg-blue-600 text-white px-2 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-1 text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="text-xs">({selectedItems.length})</span>
              </button>
            )}
            
            {isCustomerSelectionMode ? (
              <button
                onClick={() => setIsCustomerSelectionMode(false)}
                className="bg-gray-600 text-white px-2 md:px-3 py-2 rounded-lg hover:bg-gray-700 flex items-center space-x-1 md:space-x-2 text-sm"
              >
                <X className="w-4 h-4" />
                <span className="hidden md:inline">Cancel</span>
              </button>
            ) : (
              <>
                {isGuestOrder ? (
                  <div className="flex items-center space-x-2 bg-orange-100 dark:bg-orange-900/30 px-2 md:px-3 py-2 rounded-lg border border-orange-200 dark:border-orange-600 max-w-xs">
                    <User className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-orange-900 dark:text-orange-100 text-sm truncate">Guest Order</p>
                      <p className="text-xs text-orange-600 dark:text-orange-400 truncate">No customer info</p>
                    </div>
                    <button
                      onClick={() => {
                        setIsGuestOrder(false);
                        setFormData(prev => ({ ...prev, customer: '', customerPhone: '' }));
                      }}
                      className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 flex-shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : formData.customer ? (
                  <div className="flex items-center space-x-2 bg-white dark:bg-gray-600 px-2 md:px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 max-w-xs">
                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{formData.customer}</p>
                      {formData.customerPhone && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{formData.customerPhone}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setIsCustomerSelectionMode(true)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex-shrink-0"
                    >
                      <FileText className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsCustomerSelectionMode(true)}
                      className="bg-blue-600 text-white px-2 md:px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-1 md:space-x-2 text-sm"
                    >
                      <User className="w-4 h-4" />
                      <span className="hidden md:inline">Select Customer</span>
                    </button>
                    <button
                      onClick={() => {
                        setIsGuestOrder(true);
                        setFormData(prev => ({ ...prev, customer: '', customerPhone: '' }));
                      }}
                      className="bg-orange-500 text-white px-2 md:px-3 py-2 rounded-lg hover:bg-orange-600 flex items-center space-x-1 md:space-x-2 text-sm"
                    >
                      <User className="w-4 h-4" />
                      <span className="hidden md:inline">Guest Order</span>
                    </button>
                  </div>
                )}
              </>
            )}
            
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile Search Bars */}
        <div className="md:hidden border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          {!isCustomerSelectionMode ? (
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={menuSearchTerm}
                  onChange={(e) => setMenuSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search Your Menu Here"
                />
              </div>
            </div>
          ) : (
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search customers..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden relative">
          {!isCustomerSelectionMode ? (
            // Menu Mode
            <>
              {/* Left Panel - Menu */}
              <div className={`flex-1 flex flex-col overflow-hidden ${showMobileCart ? 'hidden md:flex' : 'flex'}`}>
                {/* Category Tabs */}
                <div className="px-3 md:px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
                    {getMenuCategories().map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`flex-shrink-0 px-3 md:px-4 py-2 rounded-lg font-medium text-xs transition-all duration-200 ${
                          selectedCategory === category
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                        }`}
                      >
                        <span className="whitespace-nowrap">
                          {category === 'all' ? 'All Menu' : category}
                          {category === 'all' && (
                            <span className="ml-1 text-xs opacity-75 font-normal">
                              (Most Selling)
                            </span>
                          )}
                          <span className="ml-1 text-xs opacity-75">
                            ({category === 'all' 
                              ? menuItems.length 
                              : menuItems.filter(item => item.category === category).length
                            })
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Menu Grid */}
                <div className="flex-1 overflow-y-auto p-3 md:p-4 pb-24 md:pb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3">
                    {getFilteredMenuItems().map((item) => {
                      const selectedItem = selectedItems.find(si => si.menuItem.id === item.id);
                      const quantity = selectedItem?.quantity || 0;
                      
                      return (
                        <div key={item.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:shadow-md transition-all duration-200">
                          {/* Horizontal Layout */}
                          <div className="flex items-center space-x-3">
                            {/* Item Image */}
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 relative">
                              <span className="text-lg">🍽️</span>
                              {item.image && (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="absolute inset-0 w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                                  }}
                                />
                              )}
                            </div>
                            
                            {/* Item Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-1 flex-1">{item.name}</h3>
                                {selectedCategory === 'all' && (
                                  <div className="ml-2 flex items-center space-x-1">
                                    <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                      {itemSalesData.get(item.id!) || 0}
                                    </span>
                                    <span className="text-xs text-orange-500 dark:text-orange-400">sold</span>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mb-1">{item.description || 'Delicious item'}</p>
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-gray-900 dark:text-white">₹{item.price}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-1 py-0.5 rounded">{item.category}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="mt-2">
                            {quantity > 0 ? (
                              <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2">
                                <button
                                  onClick={() => {
                                    if (selectedItem) {
                                      const index = selectedItems.findIndex(si => si.menuItem.id === item.id);
                                      updateItemQuantity(index, quantity - 1);
                                    }
                                  }}
                                  className="w-6 h-6 bg-white dark:bg-gray-600 border border-blue-200 dark:border-blue-600 rounded-md flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="font-bold text-blue-600 dark:text-blue-400 text-sm px-2">{quantity}</span>
                                <button
                                  onClick={async () => {
                                    if (selectedItem) {
                                      const index = selectedItems.findIndex(si => si.menuItem.id === item.id);
                                      
                                      try {
                                        // Check if this menu item has add-ons configured
                                        const addOnsConfig = await dataService.getMenuItemAddOns(tenantData!.id, item.id!);
                                        
                                        if (addOnsConfig && addOnsConfig.addOnCategories && addOnsConfig.addOnCategories.length > 0) {
                                          // Show add-ons selection modal for the same item
                                          setPendingMenuItem(item);
                                          setShowAddOnsModal(true);
                                        } else {
                                          // No add-ons, just increase quantity
                                          updateItemQuantity(index, quantity + 1);
                                        }
                                      } catch (error) {
                                        console.error('Error checking add-ons:', error);
                                        // Fallback to increasing quantity
                                        updateItemQuantity(index, quantity + 1);
                                      }
                                    }
                                  }}
                                  className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center text-white hover:bg-blue-700"
                                  title="Add same item (may show add-ons selection)"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => addMenuItem(item)}
                                disabled={addingItemId === item.id}
                                className="w-full bg-blue-600 text-white py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center text-sm"
                              >
                                {addingItemId === item.id ? (
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Plus className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {getFilteredMenuItems().length === 0 && (
                    <div className="text-center py-8">
                      <Search className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">No items found</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm">Try adjusting your search or category filter</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel - Cart */}
              <div className={`w-80 bg-gray-50 dark:bg-gray-700 border-l border-gray-200 dark:border-gray-600 flex flex-col ${
                showMobileCart ? 'flex md:flex' : 'hidden md:flex'
              } ${showMobileCart ? 'absolute inset-0 z-10 w-full md:w-80 md:relative' : ''}`}>
                {/* Cart Header */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Cart</h3>
                  {/* Mobile Close Button */}
                  <button
                    onClick={() => setShowMobileCart(false)}
                    className="md:hidden text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Selected Items */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {selectedItems.length > 0 ? (
                    selectedItems.map((item, index) => (
                      <div key={index} className="bg-white dark:bg-gray-600 rounded-lg p-2 border border-gray-200 dark:border-gray-500">
                        <div className="flex items-start space-x-2">
                          {/* Item Image */}
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0 relative">
                            <span className="text-xs">🍽️</span>
                            {item.menuItem.image && (
                              <img
                                src={item.menuItem.image}
                                alt={item.menuItem.name}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                                }}
                              />
                            )}
                          </div>
                          
                          {/* Item Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium text-gray-900 dark:text-white text-xs line-clamp-1">{item.menuItem.name}</h4>
                              <span className="font-bold text-xs">
                                ₹{(() => {
                                  let itemTotal = item.menuItem.price * item.quantity;
                                  if (item.addOnSelections) {
                                    item.addOnSelections.forEach(category => {
                                      category.selectedOptions.forEach(option => {
                                        itemTotal += option.price * item.quantity;
                                      });
                                    });
                                  }
                                  return itemTotal.toFixed(1);
                                })()}
                              </span>
                            </div>
                            
                            {/* Add-ons Display */}
                            {item.addOnSelections && item.addOnSelections.length > 0 && (
                              <div className="mb-1">
                                {item.addOnSelections.map((category, catIndex) => (
                                  <div key={catIndex} className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">{category.categoryName}:</span>
                                    {category.selectedOptions.map((option, optIndex) => (
                                      <span key={optIndex} className="ml-1">
                                        {option.optionName}
                                        {option.price > 0 && <span className="text-gray-500">(+₹{option.price})</span>}
                                        {optIndex < category.selectedOptions.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Quantity Controls */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                  className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center"
                                >
                                  <Minus className="w-2 h-2" />
                                </button>
                                <span className="font-medium text-xs px-1 min-w-[16px] text-center">{item.quantity}</span>
                                                            <button
                              onClick={() => addSameItemWithAddOns(index)}
                              className="w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                              title="Add same item (may show add-ons selection)"
                            >
                              <Plus className="w-2 h-2" />
                            </button>
                              </div>
                              
                              <div className="flex items-center space-x-1">
                                <span className="text-xs text-gray-500">₹{item.menuItem.price}</span>
                                <button
                                  onClick={() => removeItem(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Notes */}
                            {(item.notes || index === selectedItems.length - 1) && (
                              <input
                                type="text"
                                value={item.notes}
                                onChange={(e) => updateItemNotes(index, e.target.value)}
                                className="w-full text-xs border border-gray-200 rounded px-1 py-0.5 mt-1"
                                placeholder="Notes..."
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-xs">No items selected</p>
                      <p className="text-gray-400 text-xs">Add items from the menu</p>
                    </div>
                  )}
                </div>

                {/* Payment Summary & Actions */}
                {selectedItems.length > 0 && (
                  <div className="p-3 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600">
                    {/* Payment Summary */}
                    <div className="space-y-1 mb-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400 text-xs">Sub Total</span>
                        <span className="font-medium text-xs">₹{subtotal.toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold border-t border-gray-200 dark:border-gray-500 pt-1">
                        <span>Total Payment</span>
                        <span>₹{total.toFixed(1)}</span>
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="grid grid-cols-3 gap-1 mb-3">
                      {paymentMethods.map((method) => (
                        <button
                          key={method.value}
                          onClick={() => setSelectedPaymentMethod(method.value as any)}
                          className={`p-1 rounded border transition-all duration-200 ${
                            selectedPaymentMethod === method.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className={`w-5 h-5 ${method.color} rounded mx-auto mb-0.5 flex items-center justify-center`}>
                            <method.icon className="w-2 h-2 text-white" />
                          </div>
                          <p className="text-xs font-medium text-center leading-tight">{method.label}</p>
                        </button>
                      ))}
                    </div>

                    {/* Table Selection */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Table (Optional)
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowTableDropdown(!showTableDropdown)}
                          className="table-dropdown w-full px-3 py-2 text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm flex items-center justify-between"
                        >
                          <span className={formData.tableNumber ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}>
                            {formData.tableNumber || 'Select a table'}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showTableDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {showTableDropdown && (
                          <div className="table-dropdown absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            <div className="p-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, tableNumber: '', tableId: '' }));
                                  setShowTableDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                              >
                                No table
                              </button>
                              {tablesLoading ? (
                                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                  Loading tables...
                                </div>
                              ) : tables.length > 0 ? (
                                tables.map((table) => (
                                  <button
                                    key={table.id}
                                    type="button"
                                    onClick={() => selectTable(table)}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded flex items-center justify-between"
                                  >
                                    <span>{table.tableNumber}</span>
                                    {table.name && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {table.name}
                                      </span>
                                    )}
                                  </button>
                                ))
                                                             ) : (
                                 <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                   <div className="text-center">
                                     <div className="text-gray-400 dark:text-gray-500 mb-2">No tables available</div>
                                     <button
                                       type="button"
                                       onClick={() => {
                                         setShowTableDropdown(false);
                                         window.location.href = '/features?tab=tables';
                                       }}
                                       className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                                     >
                                       Create tables in Features page
                                     </button>
                                   </div>
                                 </div>
                               )}
                            </div>
                          </div>
                        )}
                      </div>
                                         </div>

                    {/* Order Notes */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Order Notes (Optional)
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                        placeholder="Add any special instructions..."
                        rows={2}
                      />
                    </div>

                    {/* Place Order Button */}
                    <button
                      onClick={handleSubmit}
                      disabled={loading || (!isGuestOrder && !formData.customer)}
                      className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                    >
                      {loading ? 'Processing...' : 'Place An Order'}
                    </button>

                    {error && (
                      <p className="text-red-600 dark:text-red-400 text-xs mt-1 text-center">{error}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            // Customer Selection Mode
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Left Panel - All Customers */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">All Customers</h3>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Search and select from all customers</p>
                </div>

                <div className="flex-1 overflow-y-auto p-3 md:p-4">
                  {getFilteredCustomers().length > 0 ? (
                    <div className="space-y-2 md:space-y-3">
                      {getFilteredCustomers().map((customer, index) => (
                        <button
                          key={index}
                          onClick={() => selectCustomerSuggestion(customer)}
                          className="w-full text-left p-3 md:p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
                              <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 dark:text-white text-sm md:text-base truncate">{customer.name}</p>
                                {customer.phone && (
                                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">{customer.phone}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-1 md:gap-2 mt-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                                    {customer.orderCount} orders
                                  </span>
                                  <span className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 md:px-2 py-0.5 md:py-1 rounded">
                                    ₹{customer.totalSpent.toFixed(2)}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                                    Last: {customer.lastOrderDate.toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-blue-600 dark:text-blue-400 flex-shrink-0">
                              <Plus className="w-4 h-4 md:w-5 md:h-5" />
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <User className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400">No customers found</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm">Try adjusting your search</p>
                    </div>
                  )}
                </div>

                {/* Add New Customer */}
                <div className="p-3 md:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                  <div className="space-y-2 md:space-y-3">
                    <div>
                      <input
                        type="text"
                        value={formData.customer}
                        onChange={handleInputChange}
                        name="customer"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Enter new customer name"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                      <input
                        type="tel"
                        name="customerPhone"
                        value={formData.customerPhone}
                        onChange={handleInputChange}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Phone (optional)"
                      />
                      <input
                        type="text"
                        name="tableNumber"
                        value={formData.tableNumber}
                        onChange={handleInputChange}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Table number"
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (formData.customer.trim()) {
                          setIsCustomerSelectionMode(false);
                          setCustomerSearchTerm('');
                        }
                      }}
                      disabled={!formData.customer.trim()}
                      className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Add New Customer
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Panel - Quick Customers */}
              <div className="hidden md:flex w-80 bg-gray-50 dark:bg-gray-700 border-l border-gray-200 dark:border-gray-600 flex-col">
                <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-600">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">Quick Select</h3>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Customers with open orders</p>
                </div>

                <div className="flex-1 overflow-y-auto p-3 md:p-4">
                  {quickCustomers.length > 0 ? (
                    <div className="space-y-2 md:space-y-3">
                      {quickCustomers.map((customer, index) => (
                        <button
                          key={index}
                          onClick={() => selectQuickCustomer(customer)}
                          className="w-full text-left p-2 md:p-3 border border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/30 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/50 hover:border-orange-300 dark:hover:border-orange-600 transition-all duration-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-orange-200 dark:bg-orange-700 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-orange-700 dark:text-orange-300" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white text-sm">{customer.name}</p>
                                {customer.phone && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">{customer.phone}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-orange-600 dark:text-orange-400">
                              <Clock className="w-4 h-4" />
                            </div>
                          </div>
                          
                          <div className="bg-white dark:bg-gray-600 rounded p-2 border border-orange-200 dark:border-orange-700">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 dark:text-gray-400">{customer.itemCount} items pending</span>
                              <span className="font-bold text-orange-700 dark:text-orange-400">₹{customer.remainingAmount.toFixed(2)} due</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Last order: {customer.lastOrderDate.toLocaleDateString()}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No pending orders</p>
                      <p className="text-gray-400 dark:text-gray-500 text-xs">All customers have completed orders</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Bottom Bar */}
        {!isCustomerSelectionMode && selectedItems.length > 0 && !showMobileCart && (
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 z-20 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">₹{calculateTotal().total.toFixed(1)}</p>
              </div>
              <button
                onClick={() => setShowMobileCart(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center space-x-2"
              >
                <span>Proceed</span>
                <FileText className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Add-ons Selection Modal */}
        {showAddOnsModal && pendingMenuItem && (
          <AddOnsSelectionModal
            menuItem={pendingMenuItem}
            onClose={handleAddOnsCancel}
            onConfirm={handleAddOnsConfirm}
          />
        )}
      </div>
    </div>
  );
};

export default AddOrderModal; 